import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '@core/logging';
import { UserPreferencesApiService } from './user-preferences-api.service';
import {
  DEFAULT_PREFERENCES,
  type Bcp47Locale,
  type Theme,
  type UpdateUserPreferencesPayload,
  type UserPreferences,
} from './user-preferences.types';

/**
 * Store des préférences scalaires du staff admin (theme + locale uniquement).
 *
 * - Chargement idempotent : skip si déjà chargé, coalesce les appels concurrents.
 * - Update optimiste avec rollback. Le serveur reste source de vérité — la
 *   réponse PATCH écrase l'état avec le DTO complet.
 * - Admin est CSR pur : pas de guard SSR nécessaire (cf. admin/CLAUDE.md).
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesStore {
  private readonly api = inject(UserPreferencesApiService);
  private readonly logger = inject(LoggerService);

  private readonly _preferences = signal<UserPreferences | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _isLoaded = signal(false);

  private loadInFlight: Promise<void> | null = null;

  readonly preferences = this._preferences.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isLoaded = this._isLoaded.asReadonly();

  readonly theme = computed<Theme>(() => this._preferences()?.theme ?? DEFAULT_PREFERENCES.theme);
  readonly locale = computed<Bcp47Locale>(
    () => this._preferences()?.preferredLocale ?? DEFAULT_PREFERENCES.preferredLocale
  );

  async loadPreferences(force = false): Promise<void> {
    if (!force && this._isLoaded()) return;
    if (this.loadInFlight) return this.loadInFlight;

    this.loadInFlight = this.fetchPreferences();
    try {
      await this.loadInFlight;
    } finally {
      this.loadInFlight = null;
    }
  }

  async updatePreferences(payload: UpdateUserPreferencesPayload): Promise<void> {
    const snapshot = this._preferences();
    const optimistic: UserPreferences = {
      ...(snapshot ?? DEFAULT_PREFERENCES),
      ...payload,
    };
    this._preferences.set(optimistic);
    this._error.set(null);

    try {
      const fresh = await firstValueFrom(this.api.updatePreferences(payload));
      this._preferences.set(fresh);
      this._isLoaded.set(true);
    } catch (err: unknown) {
      this._preferences.set(snapshot);
      this._error.set(this.toErrorMessage(err));
      throw err;
    }
  }

  /** Reset complet — appelé au logout. */
  clear(): void {
    this._preferences.set(null);
    this._loading.set(false);
    this._error.set(null);
    this._isLoaded.set(false);
    this.loadInFlight = null;
  }

  private async fetchPreferences(): Promise<void> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const prefs = await firstValueFrom(this.api.getMyPreferences());
      this._preferences.set(prefs);
      this._isLoaded.set(true);
    } catch (err: unknown) {
      this._error.set(this.toErrorMessage(err));
      this.logger.warn('UserPreferencesStore', 'Failed to load preferences', { err });
    } finally {
      this._loading.set(false);
    }
  }

  private toErrorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'object' && err !== null && 'message' in err) {
      const m = (err as { message: unknown }).message;
      if (typeof m === 'string') return m;
    }
    return 'An unexpected error occurred';
  }
}
